import React from 'react';
import { Typography, TypographyProps, styled } from '@mui/material';

import { IItemReadResourceV10 } from 'components/basket/model/Basket';
import { addOpacity, getCurrencyString } from 'lib/helpers';

const StyledUL = styled('ul')(({ theme }) => ({
    listStyle: 'none',
    paddingLeft: theme.spacing(1),
    '& li:before': { content: '"- "', color: addOpacity(theme.palette.text.primary, 0.6) },
    '& li': {
        textIndent: '-5px'
    }
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
    color: addOpacity(theme.palette.text.primary, 0.6),
    display: 'inline'
}));

interface IPayRecursiveModifierProductListProps {
    modifierProducts: IItemReadResourceV10[];
    orderItemQuantity?: number;
    typographyVariant?: TypographyProps['variant'];
}

export const PayRecursiveModifierProductList = ({
    modifierProducts,
    orderItemQuantity = 1,
    typographyVariant = 'caption'
}: IPayRecursiveModifierProductListProps) => {
    const productName = React.useCallback(
        (modifierProduct: IItemReadResourceV10) => {
            const modifierPrice = (modifierProduct.cost * modifierProduct.quantity) / orderItemQuantity;
            const titleAndPrice = `${modifierProduct.productName} ${
                modifierPrice ? ` (${getCurrencyString(modifierPrice)})` : ''
            }`;
            const computedQuantity = modifierProduct.quantity / orderItemQuantity;
            const productName =
                computedQuantity > 1 ? `${computedQuantity}x ${titleAndPrice}` : titleAndPrice;
            return productName;
        },
        [orderItemQuantity]
    );
    const hasNestedChildren = React.useCallback(
        (modifierProduct: IItemReadResourceV10) =>
            modifierProduct.children && modifierProduct.children.length > 0,
        []
    );
    return (
        <StyledUL>
            {modifierProducts.map(modifierProduct => (
                <React.Fragment key={modifierProduct.id}>
                    <li key={modifierProduct.id}>
                        <StyledTypography variant={typographyVariant} whiteSpace="pre-wrap">
                            {productName(modifierProduct)}
                        </StyledTypography>
                    </li>

                    {hasNestedChildren(modifierProduct) && (
                        <PayRecursiveModifierProductList
                            modifierProducts={modifierProduct.children || []}
                            orderItemQuantity={orderItemQuantity}
                            typographyVariant={typographyVariant}
                        />
                    )}
                </React.Fragment>
            ))}
        </StyledUL>
    );
};
